/**
 * Hook para gerenciamento de regras de lembretes
 * 
 * Funcionalidades:
 * - Listagem de regras por entity_type via RPC list_reminder_rules
 * - Criação/atualização via RPC upsert_reminder_rule
 * - Exclusão via RPC delete_reminder_rule
 * - Toggle ativo/inativo
 * 
 * @version 1.1.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do hook genérico
 * RELEASE: 2026-09-12 v1.1.0 - Escopo opcional por agenda (scope_entity_ids/scope_labels)
 * RELEASE: 2026-10-02 v1.2.0 - Tipo 'vistoria', campo config e variáveis de vistoria
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

const logger = createLogger('ReminderRules');

// ============================================================================
// TIPOS
// ============================================================================

/** Tipos de entidade suportados pelo módulo de lembretes */
export type ReminderEntityType = 'scheduling' | 'cobranca' | 'contrato' | 'crm' | 'vistoria';

/** Configuração específica dos lembretes de vistoria (reminder_rules.config) */
export interface InspectionReminderConfig {
  /** Status da vistoria que disparam o lembrete */
  statuses: string[];
  /** Referência do deslocamento: entrada no status, prazo de início ou de conclusão */
  anchor: 'status_entered' | 'due_start' | 'due_finish';
  /** Tipos de responsável que recebem: usuário, fila (usuário atribuído), proprietário */
  recipient_modes: Array<'user' | 'queue' | 'owner'>;
  /** Repetição: uma vez ou a cada X minutos até max_count envios */
  repeat: { mode: 'once' } | { mode: 'interval'; every_minutes: number; max_count: number };
}

/** Regra de lembrete retornada pela RPC list_reminder_rules */
export interface ReminderRule {
  id: string;
  entity_type: ReminderEntityType;
  inbox_id: string;
  inbox_name: string;
  inbox_channel_type: string;
  offset_minutes: number;
  message_type: string;
  message_template: string | null;
  template_name: string | null;
  template_language: string | null;
  template_params: Json | null;
  is_active: boolean;
  /** Configurações de throttling */
  delay_min_ms: number;
  delay_max_ms: number;
  block_size: number;
  block_delay_min_ms: number;
  block_delay_max_ms: number;
  max_per_minute: number;
  /**
   * Escopo da regra (apenas agendamento): IDs das agendas (scheduling_event_types).
   * NULL ou vazio = regra vale para TODAS as agendas.
   */
  scope_entity_ids: string[] | null;
  /** Nomes das agendas do escopo (para exibição) */
  scope_labels: string[] | null;
  /** v1.2.0: configuração específica (vistoria) */
  config?: InspectionReminderConfig | Record<string, never> | null;
  created_at: string;
  updated_at: string;
}

/** Input para criação/atualização de regra */
export interface UpsertReminderRuleInput {
  rule_id?: string;
  entity_type: ReminderEntityType;
  inbox_id: string;
  offset_minutes: number;
  message_type?: string;
  message_template?: string;
  template_name?: string;
  template_language?: string;
  template_params?: Json;
  is_active?: boolean;
  /** Throttling (valores padrão definidos no banco) */
  delay_min_ms?: number;
  delay_max_ms?: number;
  block_size?: number;
  block_delay_min_ms?: number;
  block_delay_max_ms?: number;
  max_per_minute?: number;
  /** Se true, aplica regra a registros existentes (backfill assíncrono) */
  apply_to_existing?: boolean;
  /** Escopo por agenda (apenas agendamento). Vazio/undefined = todas as agendas */
  scope_entity_ids?: string[] | null;
  /** v1.2.0: configuração específica (obrigatória para vistoria) */
  config?: InspectionReminderConfig | null;
}

// ============================================================================
// CONSTANTES - Offsets pré-definidos
// ============================================================================

/** Offsets negativos (ANTES do evento/vencimento) */
export const OFFSET_OPTIONS_BEFORE = [
  { value: -15, label: '15 minutos antes' },
  { value: -30, label: '30 minutos antes' },
  { value: -60, label: '1 hora antes' },
  { value: -120, label: '2 horas antes' },
  { value: -360, label: '6 horas antes' },
  { value: -720, label: '12 horas antes' },
  { value: -1440, label: '1 dia antes' },
  { value: -2880, label: '2 dias antes' },
  { value: -4320, label: '3 dias antes' },
];

/** Offsets positivos (APÓS o evento/referência - cobranças e vistorias) */
export const OFFSET_OPTIONS_AFTER = [
  { value: 1, label: '1 minuto depois' },
  { value: 5, label: '5 minutos depois' },
  { value: 15, label: '15 minutos depois' },
  { value: 30, label: '30 minutos depois' },
  { value: 60, label: '1 hora depois' },
  { value: 120, label: '2 horas depois' },
  { value: 360, label: '6 horas depois' },
  { value: 720, label: '12 horas depois' },
  { value: 1440, label: '1 dia depois' },
  { value: 4320, label: '3 dias depois' },
  { value: 10080, label: '7 dias depois' },
  { value: 20160, label: '14 dias depois' },
  { value: 43200, label: '30 dias depois' },
];

/** Variáveis de template por entity_type */
export const TEMPLATE_VARIABLES: Record<ReminderEntityType, { key: string; label: string }[]> = {
  scheduling: [
    { key: '{{nome_completo}}', label: 'Nome do cliente' },
    { key: '{{evento}}', label: 'Nome do evento' },
    { key: '{{data_hora}}', label: 'Data e hora' },
    { key: '{{link_reuniao}}', label: 'Link da reunião' },
    { key: '{{link_cancelamento}}', label: 'Link de cancelamento' },
  ],
  cobranca: [
    { key: '{{nome_cliente}}', label: 'Nome do cliente' },
    { key: '{{valor}}', label: 'Valor da cobrança' },
    { key: '{{vencimento}}', label: 'Data de vencimento' },
    { key: '{{dias_atraso}}', label: 'Dias em atraso' },
    { key: '{{payment_link}}', label: 'Link de pagamento' },
    { key: '{{descricao}}', label: 'Descrição da cobrança' },
  ],
  contrato: [
    { key: '{{nome_cliente}}', label: 'Nome do cliente' },
    { key: '{{contrato}}', label: 'Nome do contrato' },
    { key: '{{data_expiracao}}', label: 'Data de expiração' },
  ],
  crm: [
    { key: '{{nome_deal}}', label: 'Nome do deal' },
    { key: '{{responsavel}}', label: 'Responsável' },
    { key: '{{fase}}', label: 'Fase do pipeline' },
  ],
  vistoria: [
    { key: '{{nome_responsavel}}', label: 'Nome do responsável' },
    { key: '{{codigo_vistoria}}', label: 'Número da vistoria' },
    { key: '{{modelo_checklist}}', label: 'Modelo do checklist' },
    { key: '{{tipo_checklist}}', label: 'Tipo do checklist' },
    { key: '{{status_vistoria}}', label: 'Status da vistoria' },
    { key: '{{placa}}', label: 'Placa' },
    { key: '{{veiculo}}', label: 'Veículo' },
    { key: '{{proprietario}}', label: 'Proprietário' },
    { key: '{{prazo_inicio}}', label: 'Prazo de início' },
    { key: '{{prazo_conclusao}}', label: 'Prazo de conclusão' },
    { key: '{{dias_no_status}}', label: 'Dias no status' },
    { key: '{{link_vistoria}}', label: 'Link da vistoria (completo)' },
    { key: '{{token_link}}', label: 'Final do link (botão URL dinâmica)' },
  ],
};

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useReminderRules(entityType: ReminderEntityType) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // ---------- Listagem ----------
  const rulesQuery = useQuery({
    queryKey: ['reminder-rules', entityType, tenantId],
    queryFn: async (): Promise<ReminderRule[]> => {
      if (!tenantId) return [];

      logger.debug('Buscando regras de lembrete', { entityType });

      const { data, error } = await supabase.rpc('list_reminder_rules', {
        p_entity_type: entityType,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar regras', { error: error.message });
        throw error;
      }

      return (data || []) as unknown as ReminderRule[];
    },
    enabled: !!tenantId,
  });

  // ---------- Upsert ----------
  const upsertRule = useMutation({
    mutationFn: async (input: UpsertReminderRuleInput) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      logger.info('Salvando regra de lembrete', { 
        ruleId: input.rule_id, 
        entityType: input.entity_type,
        offsetMinutes: input.offset_minutes,
      });

      const { data, error } = await supabase.rpc('upsert_reminder_rule', {
        p_rule_id: input.rule_id || undefined,
        p_entity_type: input.entity_type,
        p_inbox_id: input.inbox_id,
        p_offset_minutes: input.offset_minutes,
        p_message_type: input.message_type || 'text',
        p_message_template: input.message_template || null,
        p_template_name: input.template_name || null,
        p_template_language: input.template_language || null,
        p_template_params: input.template_params || null,
        p_is_active: input.is_active ?? true,
        p_delay_min_ms: input.delay_min_ms,
        p_delay_max_ms: input.delay_max_ms,
        p_block_size: input.block_size,
        p_block_delay_min_ms: input.block_delay_min_ms,
        p_block_delay_max_ms: input.block_delay_max_ms,
        p_max_per_minute: input.max_per_minute,
        p_tenant_hint: tenantId,
        // v1.1.0: escopo por agenda (vazio => NULL = todas as agendas)
        p_scope_entity_ids:
          input.scope_entity_ids && input.scope_entity_ids.length > 0
            ? input.scope_entity_ids
            : null,
        // v1.2.0: configuração específica (vistoria)
        p_config: (input.config ?? null) as unknown as Json,
      });

      if (error) {
        logger.error('Erro ao salvar regra', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reminder-rules', entityType] });
      toast.success('Regra de lembrete salva com sucesso!');

      // Backfill assíncrono: aplica regra a registros existentes (fire-and-forget)
      // Erro no backfill NÃO invalida a criação da regra — tratado separadamente
      if (variables.apply_to_existing && data && tenantId) {
        const ruleId = typeof data === 'string' ? data : (data as any)?.id || data;
        logger.info('Iniciando backfill de lembretes para registros existentes', { ruleId });
        toast.info('Aplicando regra a registros existentes em segundo plano...');

        supabase.functions.invoke('backfill-reminders', {
          body: { rule_id: ruleId, tenant_id: tenantId },
        }).then(({ data: bfData, error: bfError }) => {
          if (bfError) {
            logger.error('Erro no backfill de lembretes (regra já foi criada)', { error: bfError.message });
            toast.warning(
              'Regra criada com sucesso, mas houve falha ao aplicar em registros antigos. Tente novamente mais tarde.',
              { duration: 6000 }
            );
          } else {
            const result = bfData as { total_processed?: number } | null;
            const count = result?.total_processed ?? 0;
            toast.success(`Regra aplicada a ${count} registro(s) existente(s)!`);
            queryClient.invalidateQueries({ queryKey: ['reminder-rules', entityType] });
          }
        }).catch((err) => {
          // Catch inesperado — não deve impactar UX da criação
          logger.error('Erro inesperado no backfill', { error: String(err) });
          toast.warning('Regra criada, mas não foi possível aplicar a registros antigos.');
        });
      }
    },
    onError: (error: Error) => {
      logger.error('Erro fatal ao salvar regra', { error: error.message });
      toast.error(`Erro ao salvar regra: ${error.message}`);
    },
  });

  // ---------- Toggle ativo/inativo ----------
  // Usa RPC dedicada toggle_reminder_rule (não upsert) para evitar
  // validações de campos como message_template
  const toggleRule = useMutation({
    mutationFn: async ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      logger.info('Alternando status da regra', { ruleId, isActive });

      const { error } = await supabase.rpc('toggle_reminder_rule', {
        p_rule_id: ruleId,
        p_is_active: isActive,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['reminder-rules', entityType] });
      toast.success(isActive ? 'Regra ativada!' : 'Regra desativada. Lembretes pendentes foram cancelados.');
    },
    onError: (error: Error) => {
      logger.error('Erro ao alternar regra', { error: error.message });
      toast.error(`Erro ao alterar regra: ${error.message}`);
    },
  });

  // ---------- Exclusão ----------
  const deleteRule = useMutation({
    mutationFn: async (ruleId: string) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      logger.info('Excluindo regra de lembrete', { ruleId });

      const { error } = await supabase.rpc('delete_reminder_rule', {
        p_rule_id: ruleId,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao excluir regra', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminder-rules', entityType] });
      toast.success('Regra de lembrete removida.');
    },
    onError: (error: Error) => {
      /** Tratamento específico para restrict_violation (itens vinculados) */
      const isRestrict = error.message?.includes('vinculado') || error.message?.includes('restrict_violation');
      if (isRestrict) {
        toast.error('Esta regra possui disparos vinculados. Desative-a ao invés de excluir.', {
          duration: 5000,
        });
      } else {
        toast.error(`Erro ao excluir: ${error.message}`);
      }
    },
  });

  return {
    rules: rulesQuery.data || [],
    isLoading: rulesQuery.isLoading,
    error: rulesQuery.error,
    upsertRule,
    toggleRule,
    deleteRule,
    isUpserting: upsertRule.isPending,
    isDeleting: deleteRule.isPending,
  };
}
